import pako from 'pako';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { v4 as uuid } from 'uuid';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class BingxStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}-${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.depth = depth;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL('https://open-api.bingx.com/openApi/spot/v1/common/symbols');
      const params = new URLSearchParams({
        symbol: this.pair,
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{
        data: {
          symbols: { tickSize: number }[];
        };
      }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      let prec = 1;

      if (res.data) {
        prec = normalizeNumber(res.data.data.symbols[0].tickSize).split('.')[1].length;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Bingx ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    try {
      const innerInterval = cexExchangesTimeframes[interval][ECexName.bingx];

      const url = new URL('https://open-api.bingx.com/openApi/spot/v2/market/kline');
      const params = new URLSearchParams({
        symbol: `${base.toUpperCase()}-${quote.toUpperCase()}`,
        limit: limit.toString(),
        interval: innerInterval,
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data: number[][];
      }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data.data ?? []).map(el => [
        el[0],
        normalizeNumber(el[1]),
        normalizeNumber(el[2]),
        normalizeNumber(el[3]),
        normalizeNumber(el[4]),
        normalizeNumber(el[5]),
      ]) as KLine[];
    } catch (error) {
      console.log('Bingx klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://open-api.bingx.com/openApi/spot/v1/market/trades');
      const params = new URLSearchParams({
        symbol: `${base}-${quote}`,
        limit: limit.toString(),
        timestamp: new Date().toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data?: {
          buyerMaker: boolean;
          price: string;
          qty: string;
          time: string;
        }[];
      }>('/api/v1/proxy', { params: { url: url.toString() } });

      return (result.data.data ?? []).map(item => ({
        price: item.price,
        quantity: item.qty,
        timestamp: item.time,
        side: item.buyerMaker ? 'buy' : 'sell',
      }));
    } catch (error) {
      console.log('Bingx trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();

    const intervalChannel = {
      60000: '1min',
      300000: '5min',
      900000: '15min',
      3600000: '60min',
      14400000: '4hour',
      86400000: '1day',
      604800000: '1week',
    }[interval];

    const ws = new ReconnectingWebSocket('wss://open-api-ws.bingx.com/market', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const depthChannel = `${this.pair}@incrDepth`;
    const tickerChannel = `${this.pair}@ticker`;
    const klineChannel = `${this.pair}@kline_${intervalChannel}`;
    const tradesChannel = `${this.pair}@trade`;

    this.ws.onopen = () => {
      try {
        const depthId = uuid();
        const tickerId = uuid();
        const klineId = uuid();
        const tradeId = uuid();

        ws.send(
          JSON.stringify({
            id: depthId,
            reqType: 'sub',
            dataType: depthChannel,
          }),
        );

        ws.send(
          JSON.stringify({
            id: tickerId,
            reqType: 'sub',
            dataType: tickerChannel,
          }),
        );

        ws.send(
          JSON.stringify({
            id: klineId,
            reqType: 'sub',
            dataType: klineChannel,
          }),
        );

        ws.send(
          JSON.stringify({
            id: tradeId,
            reqType: 'sub',
            dataType: tradesChannel,
          }),
        );
      } catch (error) {
        console.log('Bingx socket error', error);
      }
    };

    this.ws.onmessage = async event => {
      try {
        let rawData;
        if (event.data instanceof Blob) {
          rawData = await event.data.arrayBuffer();
        } else {
          rawData = event.data;
        }

        const data = JSON.parse(pako.ungzip(new Uint8Array(rawData), { to: 'string' }));

        if (data.dataType === depthChannel) {
          const action = data.data.action as 'all' | 'update';

          const responseAsks = Object.keys(data.data.asks).map(key => [
            key,
            data.data.asks[key],
          ]) as [string, string][];
          const responseBids = Object.keys(data.data.bids).map(key => [
            key,
            data.data.bids[key],
          ]) as [string, string][];

          const asks =
            action === 'all'
              ? mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids =
            action === 'all'
              ? mergeOrderBook(responseBids, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          this.asks = asks;
          this.bids = bids;

          this.onOrderbookUpdate(
            aggregateOrderbook(
              asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.selectedPrecision,
            ),
          );
        }

        if (data.dataType === tickerChannel) {
          const { P, v, q, c, l, h } = data.data;

          this.onTickerUpdate({
            baseVolume: normalizeNumber(v),
            change: P.replace('%', ''),
            close: normalizeNumber(c),
            high: normalizeNumber(h),
            last: c.toFixed(this.precision),
            low: normalizeNumber(l),
            quoteVolume: normalizeNumber(q),
          });
        }

        if (data.dataType === klineChannel) {
          const { t, o, h, l, c, v } = data.data.K;

          this.klineUpdate([Math.floor(t / 1000), o, h, l, c, v]);
        }

        if (data.dataType === tradesChannel) {
          const { p, m, T, q } = data.data;

          this.onTradeUpdate({
            price: p,
            quantity: q,
            timestamp: T,
            side: m ? 'sell' : 'buy',
          });
        }

        if (data.ping) {
          this.ws?.send(
            JSON.stringify({
              pong: data.ping,
              time: data.time,
            }),
          );
        }
      } catch (error) {
        console.log('Bingx socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
      ),
    );
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new BingxStrategy(args);

    await instance.fetch();

    return instance;
  };
}
